import type { CategoryDef, ScoreContext, VariantDef } from '../types';
import { UPPER_IDS_NONE } from './wordShared';
import { wordValue } from '../words';

/**
 * Seven lettered dice. Distribution leans on common letters and guarantees
 * vowels on several dice so a playable word is usually within reach.
 */
export const WORD_DICE: string[][] = [
  ['A', 'E', 'I', 'O', 'U', 'Y'],
  ['A', 'E', 'O', 'S', 'T', 'R'],
  ['E', 'I', 'N', 'L', 'D', 'H'],
  ['A', 'U', 'C', 'M', 'P', 'B'],
  ['O', 'R', 'G', 'K', 'F', 'W'],
  ['E', 'T', 'N', 'V', 'Y', 'X'],
  ['I', 'S', 'L', 'J', 'Q', 'Z'],
];

const VOWELS = new Set(['A', 'E', 'I', 'O', 'U']);

function validWord(ctx: ScoreContext): string | null {
  return ctx.word && ctx.wordValid ? ctx.word : null;
}

function special(
  id: string,
  name: string,
  short: string,
  hint: string,
  bonus: number,
  test: (word: string) => boolean
): CategoryDef {
  return {
    id,
    name,
    short,
    section: 'lower',
    hint,
    score: ctx => {
      const w = validWord(ctx);
      return w && test(w) ? wordValue(w) + bonus : 0;
    },
  };
}

function lengthCategory(len: number, bonus: number): CategoryDef {
  return special(
    `w${len}`,
    `${len}-letter word`,
    `${len}L`,
    bonus
      ? `A ${len}-letter word: letter values plus ${bonus}`
      : `A ${len}-letter word: letter values`,
    bonus,
    w => w.length === len
  );
}

export const word: VariantDef = {
  id: 'word',
  name: 'Word',
  tagline: 'Spell it out',
  description:
    'Seven lettered dice. Spell a word from the letters you roll and score its letter values in a box for its length or shape. Using all seven dice to spell a seven-letter word is the Yahtzee of this game: letter values plus 50.',
  family: 'official',
  diceCount: 7,
  maxRolls: 3,
  columns: [1],
  categories: [
    lengthCategory(3, 0),
    lengthCategory(4, 5),
    lengthCategory(5, 10),
    lengthCategory(6, 20),
    lengthCategory(7, 50),
    special(
      'vowelly',
      'Vowel Heavy',
      'Vow',
      'A word with three or more vowels: letter values plus 10',
      10,
      w => [...w].filter(ch => VOWELS.has(ch)).length >= 3
    ),
    special(
      'doubled',
      'Double Letter',
      'Dbl',
      'A word containing the same letter twice: letter values plus 10',
      10,
      w => new Set(w).size < w.length
    ),
    special(
      'pricey',
      'Big Ticket',
      'Big',
      'A word using a letter worth 4 or more: letter values plus 10',
      10,
      w => [...w].some(ch => 'FHVWYKJXQZ'.includes(ch))
    ),
    special(
      'anyWord',
      'Any Word',
      'Any',
      'Any valid word: letter values',
      0,
      () => true
    ),
  ],
  upperCategoryIds: UPPER_IDS_NONE,
  upperBonus: () => 0,
  upperBonusHint: null,
  fiveKind: null,
  sharedRolls: false,
  sequential: false,
  streak: null,
  powers: null,
  letterFaces: WORD_DICE,
  pipColors: null,
  servedInstantWin: null,
  rules: [
    'Roll seven lettered dice up to three times, holding letters you want to keep.',
    'Spell one word from your letters (each die used at most once) and pick a box it qualifies for.',
    'Letter values are Scrabble-style: common letters 1, rare letters up to 10.',
    'Length boxes add a bonus: 4 letters +5, 5 letters +10, 6 letters +20. A seven-letter word uses every die and adds 50.',
    'Scratch any open box for zero, with or without a word.',
  ],
};
